import {
  breakpoint,
  ButtonLink,
  Button,
  CardContent,
  CardFooter,
  CardLarge,
  CardSeparator,
  FormGroup,
  FormRow,
  Heading1,
  Text,
  Size,
  CardHeader,
  useTestId
} from '@cmctechnology/phoenix-stockbroking-web-design';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { OPEN_EXTERNAL_URL_TARGET } from '../constants/urlConstants';
import { AccountTypeInput, IsIndividualAccountType, termsAndConditions, termsAndConditionsANZM } from '../models/application';
import { acceptTnCAndSubmitApplication, openExternalUrl, submitPromoCode } from '../Store/Actions';
import { IPreviousPage } from '../models/IPageNavigation';
import { GoBackIcon } from '../Components/GoBackIcon';
import { PromoCodeInput } from '../Components/PromoCodeInput';
import { usePromoCodeValidator } from '../hooks/usePromoCodeValidator';
import { IStore } from '../Store/Store';

const TextWithBulletPoint = styled(Text)`
  display: list-item;
`;

const FormGroupWithLeftPadding = styled(FormGroup)`
  padding-left: 2rem;
`;

const LeftJustifiedFormRow = styled(FormRow)`
  @media (max-width: ${breakpoint.mobile}) {
    justify-content: left;
  }
`;
export interface ITermsConfirmationProps extends IPreviousPage {
  onAccept: () => void;
}

export const TermsConfirmation: React.FC<ITermsConfirmationProps> = ({ onAccept, onPrevious, ...rest }) => {
  const { generateTestId } = useTestId(rest, `au.legalDeclaration`);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [promoCode, setPromoCode] = useState(window?.localStorage?.PromoCode ?? '');
  const accountType = useSelector((store: IStore) => store.local.accountType || AccountTypeInput.Individual);

  const { promoCodeValidator } = usePromoCodeValidator(promoCode);

  useEffect(() => {
    promoCodeValidator.validate();
  }, []);

  const onAcceptClicked = () => {
    dispatch(submitPromoCode(promoCodeValidator.value));
    dispatch(acceptTnCAndSubmitApplication());
    onAccept();
  };

  return (
    <CardLarge>
      <CardHeader>
        <GoBackIcon onClick={onPrevious} />
        <Heading1>{t('Almost done!')}</Heading1>
      </CardHeader>
      <CardSeparator />
      <CardContent>
        <FormRow>
          <Text size={Size.Large}>
            {IsIndividualAccountType(accountType)
              ? t('Please read the following documents before you apply for a CMC Invest account:')
              : t('Please read the following documents before you apply for a CMC Markets Invest account:')}
          </Text>
        </FormRow>
        <FormGroup {...generateTestId(`tnc.group`)}>
          {(IsIndividualAccountType(accountType) ? termsAndConditionsANZM : termsAndConditions).map((term) => (
            <React.Fragment key={term.key}>
              <LeftJustifiedFormRow>
                <ButtonLink style={{ textAlign: 'left' }} onClick={() => openExternalUrl(term.url, OPEN_EXTERNAL_URL_TARGET)} {...generateTestId(term.key)}>
                  {term.label(t)}
                </ButtonLink>
              </LeftJustifiedFormRow>
            </React.Fragment>
          ))}
        </FormGroup>
        <FormGroup>
          <Text size={Size.Large} textAlign='left'>
            <Trans t={t}>
              By clicking <b>Accept + Confirm:</b>
            </Trans>
          </Text>
        </FormGroup>
        <FormGroup {...generateTestId(`group`)}>
          {IsIndividualAccountType(accountType) ? (
            <>
              <FormGroupWithLeftPadding>
                <TextWithBulletPoint size={Size.Large} textAlign='left' {...generateTestId(`acknowledgement`)}>
                  {t(
                    'You confirm you have read, understood and agree to be bound by the Terms & Conditions, FSG, Best Execution Policy and other associated documents from time to time, including the Website Terms & Conditions and Privacy Statement which you agree will be made available to you digitally on our website '
                  )}
                  <a
                    href='https://www.cmcmarkets.com/en-au/legal-documents/stockbroking-legal-documents'
                    target='_blank'
                    rel='noreferrer'
                    {...generateTestId(`acknowledgement.sbLegal`)}
                  >
                    {t('here')}
                  </a>
                  {t(' and ')}
                  <a
                    href='https://www.cmcmarkets.com/en-au/legal-documents/other-legal-documents'
                    target='_blank'
                    rel='noreferrer'
                    {...generateTestId(`acknowledgement.otherLegal`)}
                  >
                    {t('here')}
                  </a>
                  {t('.')}
                </TextWithBulletPoint>
              </FormGroupWithLeftPadding>
              <FormGroupWithLeftPadding>
                <TextWithBulletPoint size={Size.Large} textAlign='left' {...generateTestId(`authorisation`)}>
                  {t('You authorise CMC Markets Stockbroking Limited (')}
                  <b>{t('CMC Invest')}</b>
                  {t(
                    ') to hold funds on trust on your behalf in an ANZ Client Money Management account with Australia and New Zealand Banking Group Limited ('
                  )}
                  <b>{t('ANZ')}</b>
                  {t(') for the purpose of or in connection with settling transactions on your CMC Invest account pursuant to the Terms & Conditions.')}
                </TextWithBulletPoint>
              </FormGroupWithLeftPadding>
              <FormGroupWithLeftPadding>
                <TextWithBulletPoint size={Size.Large} textAlign='left' {...generateTestId(`consent`)}>
                  {t(
                    'You confirm that you are fully authorised to provide the personal details presented, which relate to you and consent to CMC Invest collecting your personal information for the purposes of this account application and disclosing it to third parties in order to verify your identity and/or for any other purpose described in the Privacy Statement.  You acknowledge that information about you, including personal information, will be given to ANZ and that information will be dealt with in accordance with the ANZ Privacy Statement.'
                  )}
                </TextWithBulletPoint>
              </FormGroupWithLeftPadding>
              <FormGroupWithLeftPadding>
                <TextWithBulletPoint size={Size.Large} textAlign='left' {...generateTestId(`declaration`)}>
                  {t(
                    'You declare that the information provided in the application is true and correct and that you understand it is an offence under the Anti-Money Laundering and Counter Terrorism Financial Act 2006 (Cth) to make false or misleading statements. You will notify CMC Invest immediately if any of that information materially changes or ceases to be true and correct.'
                  )}
                </TextWithBulletPoint>
              </FormGroupWithLeftPadding>
            </>
          ) : (
            <>
              <FormGroupWithLeftPadding>
                <TextWithBulletPoint size={Size.Large} textAlign='left' {...generateTestId(`acknowledgement`)}>
                  {t(
                    'I acknowledge that I have read, understood and agree to the above documents (including the CHESS Sponsorship Agreement contained in Part G of the Terms and Conditions)'
                  )}
                </TextWithBulletPoint>
              </FormGroupWithLeftPadding>
              <FormGroupWithLeftPadding>
                <TextWithBulletPoint size={Size.Large} textAlign='left' {...generateTestId(`authorisation`)}>
                  {t(
                    'I authorise CMC Markets Stockbroking to open an ANZ Cash Active account for the purpose of settling transactions on my CMC Markets Invest account pursuant to the CMC Markets Invest Terms and Conditions'
                  )}
                </TextWithBulletPoint>
              </FormGroupWithLeftPadding>
              <FormGroupWithLeftPadding>
                <TextWithBulletPoint size={Size.Large} textAlign='left' {...generateTestId(`consent`)}>
                  {t(
                    'I consent to CMC Markets Stockbroking collecting my personal information for the purposes of this account application and disclosing it to third parties in order to verify my identity'
                  )}
                </TextWithBulletPoint>
              </FormGroupWithLeftPadding>
            </>
          )}
        </FormGroup>
      </CardContent>
      <CardSeparator />
      <CardContent>
        <PromoCodeInput validator={promoCodeValidator} promoCode={promoCode} setPromoCode={setPromoCode} />
      </CardContent>
      <CardFooter>
        <FormGroup>
          <FormRow>
            <Button
              label={t('Accept + Confirm')}
              onClick={() => onAcceptClicked()}
              disabled={promoCodeValidator.value !== '' && (promoCodeValidator.validating || promoCodeValidator.invalid)}
              {...generateTestId(`accept`)}
            />
          </FormRow>
        </FormGroup>
      </CardFooter>
    </CardLarge>
  );
};
